import React, { useState, useEffect, useCallback } from 'react';
import { AZURE } from '../../services/api';
import { COLORS } from '../../components/governance/governanceTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import {
  Lightbulb, FileText, BookOpen, Palette, Send, Activity,
  Eye, CheckCircle, XCircle, Clock, TrendingUp, Zap,
  ChevronRight, RefreshCw, ExternalLink, AlertTriangle,
  X, Sparkles, Loader2, BarChart3, ArrowRight, Edit3
} from 'lucide-react';

const EDITORIAL_LABELS = {
  pt: {
    detectSwimming: 'Detectar Natação', detectFootball: 'Detectar Futebol', detecting: 'Detectando...',
    allSports: 'Todos os esportes', swimming: 'Natação', football: 'Futebol', refresh: 'Atualizar',
    colInsight: 'Insight', colSport: 'Esporte', colType: 'Tipo', colConfidence: 'Confiança',
    colPriority: 'Prioridade', colStatus: 'Status', colCreated: 'Criado',
    noInsights: 'Nenhum insight detectado ainda. Use os botões acima para detectar.',
    detailTitle: 'Detalhe do Insight', confidence: 'Confiança', priority: 'Prioridade',
    insightData: 'Dados do Insight', goDrafts: 'Ir para Drafts e editar',
    subtitle: 'Pipeline editorial completo: detectar → curar → gerar → revisar → publicar',
    detectors: 'Detectores ativos', channels: 'Canais de publicação', endpoints: 'Endpoints ativos',
    healthOk: 'Engine operacional', healthOff: 'Engine offline',
  },
  en: {
    detectSwimming: 'Detect Swimming', detectFootball: 'Detect Football', detecting: 'Detecting...',
    allSports: 'All sports', swimming: 'Swimming', football: 'Football', refresh: 'Refresh',
    colInsight: 'Insight', colSport: 'Sport', colType: 'Type', colConfidence: 'Confidence',
    colPriority: 'Priority', colStatus: 'Status', colCreated: 'Created',
    noInsights: 'No insights detected yet. Use the buttons above to detect.',
    detailTitle: 'Insight Detail', confidence: 'Confidence', priority: 'Priority',
    insightData: 'Insight Data', goDrafts: 'Go to Drafts and edit',
    subtitle: 'Full editorial pipeline: detect → curate → generate → review → publish',
    detectors: 'Active detectors', channels: 'Publishing channels', endpoints: 'Active endpoints',
    healthOk: 'Engine operational', healthOff: 'Engine offline',
  }
};

// ─── Tab definitions ────────────────────────────────────────
const TABS = [
  { id: 'insights',     label: 'Insights',       icon: Lightbulb },
  { id: 'drafts',       label: 'Drafts',         icon: FileText },
  { id: 'publish',      label: 'Publish Queue',  icon: Send },
  { id: 'templates',    label: 'Templates',       icon: Palette },
  { id: 'library',      label: 'Library',         icon: BookOpen },
  { id: 'agent-runs',   label: 'Agent Runs',      icon: Activity },
];

// ─── Status badges ──────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    detected:             { bg: '#dbeafe', text: '#1e40af', label: 'Detected' },
    editorial_candidate:  { bg: '#fef3c7', text: '#92400e', label: 'Candidate' },
    draft_generated:      { bg: '#e0e7ff', text: '#3730a3', label: 'Draft Generated' },
    published:            { bg: '#d1fae5', text: '#065f46', label: 'Published' },
    archived:             { bg: '#f3f4f6', text: '#6b7280', label: 'Archived' },
    pending:              { bg: '#fef3c7', text: '#92400e', label: 'Pending' },
    approved:             { bg: '#d1fae5', text: '#065f46', label: 'Approved' },
    rejected:             { bg: '#fee2e2', text: '#991b1b', label: 'Rejected' },
    completed:            { bg: '#d1fae5', text: '#065f46', label: 'Completed' },
    failed:               { bg: '#fee2e2', text: '#991b1b', label: 'Failed' },
    completed_with_errors:{ bg: '#fef3c7', text: '#92400e', label: 'Partial' },
    running:              { bg: '#dbeafe', text: '#1e40af', label: 'Running' },
  };
  const s = map[status] || { bg: '#f3f4f6', text: '#6b7280', label: status };
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      {s.label}
    </span>
  );
};

const PriorityDot = ({ priority }) => {
  const colors = { high: '#dc2626', medium: '#f59e0b', low: '#9ca3af' };
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full mr-1.5"
      style={{ backgroundColor: colors[priority] || '#9ca3af' }}
      title={priority}
    />
  );
};

// ─── Insights Tab ───────────────────────────────────────────
const InsightsTab = ({ onNavigateToDrafts, t }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [sportFilter, setSportFilter] = useState('');
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (sportFilter) params.sport = sportFilter;
      const { data } = await AZURE.get('/editorial/insights', { params });
      setInsights(data.data || []);
    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  }, [sportFilter]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const handleDetect = async (sport) => {
    setDetecting(true);
    try {
      await AZURE.post('/editorial/insights/detect', { sport, persist: true });
      await fetchInsights();
    } catch (err) {
      console.error('Detection error:', err);
    } finally {
      setDetecting(false);
    }
  };

  const handleSelectInsight = async (ins) => {
    setGenerateResult(null);
    try {
      const { data } = await AZURE.get(`/editorial/insights/${ins.id}`);
      setSelectedInsight(data.data || ins);
    } catch {
      setSelectedInsight(ins);
    }
  };

  const handleGenerate = async () => {
    if (!selectedInsight) return;
    setGenerating(true);
    setGenerateResult(null);
    try {
      const { data } = await AZURE.post('/editorial/content/generate', {
        insight_id: selectedInsight.id,
        channels: ['website', 'linkedin'],
        persist: true,
      });
      if (data.success) {
        setGenerateResult({ success: true, drafts: data.data?.drafts || data.data || [] });
        // Refresh insights to reflect status change
        await fetchInsights();
      } else {
        setGenerateResult({ success: false, error: data.error || 'Erro desconhecido' });
      }
    } catch (err) {
      setGenerateResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setGenerating(false);
    }
  };

  // Parse JSON payload safely
  const parsePayload = (insight) => {
    if (!insight) return null;
    try {
      if (typeof insight.payload_json === 'string') return JSON.parse(insight.payload_json);
      if (typeof insight.payload_json === 'object') return insight.payload_json;
    } catch { /* ignore */ }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => handleDetect('swimming')}
          disabled={detecting}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
        >
          <Zap className="w-4 h-4" /> {detecting ? t.detecting : t.detectSwimming}
        </button>
        <button
          onClick={() => handleDetect('football')}
          disabled={detecting}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
        >
          <Zap className="w-4 h-4" /> {detecting ? t.detecting : t.detectFootball}
        </button>
        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
        >
          <option value="">{t.allSports}</option>
          <option value="swimming">{t.swimming}</option>
          <option value="football">{t.football}</option>
        </select>
        <button
          onClick={fetchInsights}
          className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-900 text-sm"
        >
          <RefreshCw className="w-3.5 h-3.5" /> {t.refresh}
        </button>
        <span className="text-xs text-gray-400 ml-auto">{insights.length} insights</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Table (3/5 width on desktop) */}
        <div className={`${selectedInsight ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <th className="px-4 py-3 text-left">{t.colInsight}</th>
                    <th className="px-4 py-3 text-left">{t.colSport}</th>
                    <th className="px-4 py-3 text-left">{t.colType}</th>
                    <th className="px-4 py-3 text-center">{t.colConfidence}</th>
                    <th className="px-4 py-3 text-center">{t.colPriority}</th>
                    <th className="px-4 py-3 text-center">{t.colStatus}</th>
                    <th className="px-4 py-3 text-left">{t.colCreated}</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.map((ins) => (
                    <tr
                      key={ins.id}
                      onClick={() => handleSelectInsight(ins)}
                      className={`border-t border-gray-100 cursor-pointer transition-colors ${
                        selectedInsight?.id === ins.id
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">
                        <div className="flex items-center gap-2">
                          {ins.title}
                          <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {ins.sport === 'swimming' ? '🏊 Natação' : '⚽ Futebol'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{ins.insight_type}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-mono text-xs ${(ins.confidence_score || 0) >= 0.8 ? 'text-green-600' : (ins.confidence_score || 0) >= 0.7 ? 'text-yellow-600' : 'text-red-500'}`}>
                          {((ins.confidence_score || 0) * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <PriorityDot priority={ins.editorial_priority} />
                        <span className="text-xs text-gray-500">{ins.editorial_priority}</span>
                      </td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={ins.status} /></td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {ins.created_at ? new Date(ins.created_at).toLocaleDateString('pt-BR') : '—'}
                      </td>
                    </tr>
                  ))}
                  {insights.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                        {t.noInsights}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail panel (2/5 width on desktop) */}
        {selectedInsight && (
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-4">
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100" style={{ backgroundColor: '#f0fdf4' }}>
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" style={{ color: COLORS.primary }} />
                  <span className="text-sm font-bold" style={{ color: COLORS.primary }}>{t.detailTitle}</span>
                </div>
                <button
                  onClick={() => { setSelectedInsight(null); setGenerateResult(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Title & meta */}
                <div>
                  <h3 className="font-bold text-gray-900 text-sm leading-snug">{selectedInsight.title}</h3>
                  {selectedInsight.summary && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-3">{selectedInsight.summary}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                      {selectedInsight.sport === 'swimming' ? '🏊 Natação' : '⚽ Futebol'}
                    </span>
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                      {selectedInsight.insight_type}
                    </span>
                    <StatusBadge status={selectedInsight.status} />
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                    <p className={`text-lg font-bold ${(selectedInsight.confidence_score || 0) >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {((selectedInsight.confidence_score || 0) * 100).toFixed(0)}%
                    </p>
                    <p className="text-xs text-gray-500">{t.confidence}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <PriorityDot priority={selectedInsight.editorial_priority} />
                      <p className="text-sm font-bold text-gray-700 capitalize">{selectedInsight.editorial_priority}</p>
                    </div>
                    <p className="text-xs text-gray-500">{t.priority}</p>
                  </div>
                </div>

                {/* Payload preview */}
                {parsePayload(selectedInsight) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Dados do Insight</p>
                    <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                      <pre className="text-xs text-gray-600 whitespace-pre-wrap break-words">
                        {JSON.stringify(parsePayload(selectedInsight), null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Insight key */}
                {selectedInsight.insight_key && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <BarChart3 className="w-3 h-3" />
                    <span>Detector: <code className="bg-gray-100 px-1 rounded">{selectedInsight.insight_key}</code></span>
                  </div>
                )}

                {/* Generate result */}
                {generateResult && (
                  <div className={`rounded-lg p-3 ${generateResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    {generateResult.success ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <p className="text-sm font-semibold text-green-800">
                            {generateResult.drafts.length} draft(s) gerado(s)!
                          </p>
                        </div>
                        <ul className="space-y-1">
                          {generateResult.drafts.map((d, i) => (
                            <li key={i} className="text-xs text-green-700 flex items-center gap-1">
                              <ArrowRight className="w-3 h-3" />
                              {d.channel === 'website' ? '🌐 Website' : '💼 LinkedIn'} — {d.title}
                            </li>
                          ))}
                        </ul>
                        {onNavigateToDrafts && (
                          <button
                            onClick={onNavigateToDrafts}
                            className="flex items-center gap-1 mt-1 text-xs font-semibold text-green-700 hover:text-green-900"
                          >
                            <Edit3 className="w-3 h-3" /> Ir para Drafts e editar
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <p className="text-sm text-red-700">{generateResult.error}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Generate button */}
                <button
                  onClick={handleGenerate}
                  disabled={generating || selectedInsight.status === 'draft_generated' || selectedInsight.status === 'published'}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: generating ? '#6b7280' : COLORS.primary,
                  }}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Gerando conteúdo com GPT-4.1...
                    </>
                  ) : selectedInsight.status === 'draft_generated' ? (
                    <>
                      <CheckCircle className="w-4 h-4" /> Draft já gerado
                    </>
                  ) : selectedInsight.status === 'published' ? (
                    <>
                      <CheckCircle className="w-4 h-4" /> Já publicado
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> Gerar Conteúdo (Website + LinkedIn)
                    </>
                  )}
                </button>

                {selectedInsight.status !== 'draft_generated' && selectedInsight.status !== 'published' && (
                  <p className="text-xs text-gray-400 text-center">
                    O GPT-4.1 gerará um estudo para o site e um post para LinkedIn com base nos dados deste insight.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Drafts Tab ─────────────────────────────────────────────
const DraftsTab = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await AZURE.get('/editorial/drafts');
      setDrafts(data.data || []);
    } catch (err) {
      console.error('Error fetching drafts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const handleSelectDraft = (d) => {
    setSelectedDraft(d);
    setEditMode(false);
    setPreviewMode(false);
    setSaveMsg(null);
  };

  const handleEnterEdit = () => {
    if (!selectedDraft) return;
    setEditTitle(selectedDraft.title || '');
    setEditBody(selectedDraft.body || '');
    setEditMode(true);
    setPreviewMode(false);
    setSaveMsg(null);
  };

  const handleSave = async () => {
    if (!selectedDraft) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      await AZURE.patch(`/editorial/drafts/${selectedDraft.id}`, {
        title: editTitle,
        body: editBody,
      });
      setSaveMsg({ type: 'success', text: 'Salvo com sucesso!' });
      // Update local state
      const updated = { ...selectedDraft, title: editTitle, body: editBody };
      setSelectedDraft(updated);
      setDrafts(prev => prev.map(d => d.id === updated.id ? { ...d, title: editTitle } : d));
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar' });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (draftId, newStatus, notes = '') => {
    try {
      await AZURE.patch(`/editorial/drafts/${draftId}`, {
        review_status: newStatus,
        review_notes: notes || undefined,
      });
      await fetchDrafts();
      setSelectedDraft(null);
      setEditMode(false);
    } catch (err) {
      console.error('Error updating draft:', err);
    }
  };

  // Simple markdown-to-HTML renderer for preview
  const renderMarkdown = (md) => {
    if (!md) return '';
    return md
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold text-gray-900 mt-4 mb-1">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-gray-900 mt-5 mb-2">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-xl font-extrabold text-gray-900 mt-6 mb-2">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-gray-700">$1</li>')
      .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 list-decimal text-gray-700">$2</li>')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">{drafts.length} drafts</span>
        <button onClick={fetchDrafts} className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-900 text-sm">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Draft list (sidebar) */}
        <div className={`${editMode ? 'lg:col-span-1' : 'lg:col-span-2'} bg-white rounded-xl border border-gray-200 overflow-hidden`}>
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
              {drafts.map((d) => (
                <button
                  key={d.id}
                  onClick={() => handleSelectDraft(d)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${selectedDraft?.id === d.id ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm">{d.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {d.channel === 'website' ? '🌐' : '💼'} {d.sport === 'swimming' ? '🏊' : '⚽'} {editMode ? '' : `• ${d.model_used}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge status={d.review_status} />
                    </div>
                  </div>
                </button>
              ))}
              {drafts.length === 0 && (
                <p className="px-4 py-8 text-center text-gray-400 text-sm">Nenhum draft gerado.</p>
              )}
            </div>
          )}
        </div>

        {/* Canvas / Preview area */}
        <div className={`${editMode ? 'lg:col-span-4' : 'lg:col-span-3'} bg-white rounded-xl border border-gray-200 overflow-hidden`}>
          {selectedDraft ? (
            editMode ? (
              /* ═══ CANVAS EDITOR ═══ */
              <div className="flex flex-col h-full">
                {/* Editor toolbar */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <Edit3 className="w-4 h-4" style={{ color: COLORS.primary }} />
                    <span className="text-sm font-bold" style={{ color: COLORS.primary }}>Editor Canvas</span>
                    <span className="text-xs text-gray-400">
                      {selectedDraft.channel === 'website' ? '🌐 Website Article' : '💼 LinkedIn Post'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPreviewMode(!previewMode)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        previewMode
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Eye className="w-3 h-3" /> {previewMode ? 'Editando' : 'Preview'}
                    </button>
                    <button
                      onClick={() => { setEditMode(false); setPreviewMode(false); }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="p-4 flex-1 overflow-y-auto space-y-4">
                  {/* Title input */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Título</label>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                      placeholder="Título do artigo..."
                    />
                  </div>

                  {/* Body editor or preview */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">
                      Conteúdo {previewMode ? '(Preview)' : '(Markdown)'}
                    </label>
                    {previewMode ? (
                      <div
                        className="prose prose-sm max-w-none border border-gray-200 rounded-lg p-4 bg-white min-h-[400px] overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(editBody) }}
                      />
                    ) : (
                      <textarea
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 font-mono focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none"
                        style={{ minHeight: '400px' }}
                        placeholder="Escreva em Markdown..."
                      />
                    )}
                  </div>

                  {/* Save message */}
                  {saveMsg && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      saveMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {saveMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                      {saveMsg.text}
                    </div>
                  )}
                </div>

                {/* Bottom action bar */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                  <p className="text-xs text-gray-400">
                    {editBody.split(/\s+/).filter(Boolean).length} palavras • {editBody.length} caracteres
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setEditMode(false); setPreviewMode(false); }}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-5 py-2 text-white rounded-lg text-sm font-bold disabled:opacity-50"
                      style={{ backgroundColor: COLORS.primary }}
                    >
                      {saving ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Salvando...</>
                      ) : (
                        <><CheckCircle className="w-4 h-4" /> Salvar Alterações</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ═══ READ-ONLY PREVIEW (original) ═══ */
              <div className="p-4 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-gray-900">{selectedDraft.title}</h3>
                    <p className="text-xs text-gray-400 mt-1">
                      Canal: {selectedDraft.channel} • Tokens: {selectedDraft.prompt_tokens || 0}+{selectedDraft.completion_tokens || 0} • {selectedDraft.model_used}
                    </p>
                  </div>
                  <button
                    onClick={handleEnterEdit}
                    className="flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm font-bold shrink-0"
                    style={{ backgroundColor: COLORS.primary }}
                  >
                    <Edit3 className="w-4 h-4" /> Editar no Canvas
                  </button>
                </div>

                {selectedDraft.excerpt && (
                  <p className="text-sm text-gray-600 italic border-l-2 border-gray-200 pl-3">{selectedDraft.excerpt}</p>
                )}

                <div
                  className="prose prose-sm max-w-none max-h-96 overflow-y-auto border border-gray-100 rounded-lg p-4 bg-gray-50"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedDraft.body || '') }}
                />

                {/* Action buttons */}
                {selectedDraft.review_status === 'pending' && (
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => handleStatusChange(selectedDraft.id, 'approved')}
                      className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4" /> Aprovar
                    </button>
                    <button
                      onClick={() => handleStatusChange(selectedDraft.id, 'rejected', 'Rejeitado via governança')}
                      className="flex items-center gap-1 px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-100"
                    >
                      <XCircle className="w-4 h-4" /> Rejeitar
                    </button>
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Eye className="w-8 h-8 mb-2" />
              <p className="text-sm">Selecione um draft para visualizar ou editar</p>
              <p className="text-xs mt-1">Clique em "Editar no Canvas" para personalizar o conteúdo</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Publish Queue Tab ──────────────────────────────────────
const PublishQueueTab = () => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await AZURE.get('/editorial/publish-queue');
      setQueue(data.data || []);
    } catch (err) {
      console.error('Error fetching publish queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handlePublish = async (draftId) => {
    setPublishing(draftId);
    try {
      const { data } = await AZURE.post('/editorial/publish', { draft_id: draftId });
      if (data.success) {
        await fetchQueue();
      }
    } catch (err) {
      console.error('Publish error:', err);
    } finally {
      setPublishing(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Drafts aprovados prontos para publicação na área de estudos.
        </p>
        <button onClick={fetchQueue} className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-900 text-sm">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : queue.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
          <Send className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">Nenhum draft na fila de publicação.</p>
          <p className="text-xs mt-1">Aprove drafts na aba "Drafts" primeiro.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                <th className="px-4 py-3 text-left">Título</th>
                <th className="px-4 py-3 text-left">Esporte</th>
                <th className="px-4 py-3 text-center">Confiança</th>
                <th className="px-4 py-3 text-center">Prioridade</th>
                <th className="px-4 py-3 text-left">Aprovado em</th>
                <th className="px-4 py-3 text-center">Ação</th>
              </tr>
            </thead>
            <tbody>
              {queue.map((item) => (
                <tr key={item.draft_id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{item.title}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {item.sport === 'swimming' ? '🏊 Natação' : '⚽ Futebol'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-mono text-xs">
                      {((item.confidence_score || 0) * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <PriorityDot priority={item.editorial_priority} />
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {item.reviewed_at ? new Date(item.reviewed_at).toLocaleDateString('pt-BR') : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handlePublish(item.draft_id)}
                      disabled={publishing === item.draft_id}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 mx-auto"
                    >
                      <Send className="w-3 h-3" />
                      {publishing === item.draft_id ? 'Publicando...' : 'Publicar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Templates Tab ──────────────────────────────────────────
const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await AZURE.get('/editorial/templates');
        setTemplates(data.data || []);
      } catch (err) {
        console.error('Error fetching templates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Templates de prompt utilizados pelo GPT-4.1 para gerar conteúdo.
      </p>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Palette className="w-5 h-5" style={{ color: COLORS.primary }} />
                <h3 className="font-bold text-gray-900">{t.content_type}</h3>
                <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {t.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p>📡 Canal: <span className="font-medium">{t.channel}</span></p>
                <p>📝 Max palavras: <span className="font-medium">{t.max_words}</span></p>
                <p>🎭 Tom: <span className="font-medium">{t.tone}</span></p>
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <p className="col-span-2 text-center text-gray-400 py-8">Nenhum template encontrado.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Library Tab (placeholder for Sprint 2) ─────────────────
const LibraryTab = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
    <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-300" />
    <h3 className="font-bold text-gray-700 mb-2">Integração com Biblioteca</h3>
    <p className="text-sm text-gray-500 max-w-md mx-auto">
      Em breve: autorize itens da Digital Sports Library para uso editorial.
      O agente poderá gerar conteúdo híbrido combinando insights + referências técnicas.
    </p>
    <div className="mt-4 flex justify-center gap-3">
      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">Sprint 2</span>
      <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">3 modos: insight / library / hybrid</span>
    </div>
  </div>
);

// ─── Agent Runs Tab ─────────────────────────────────────────
const AgentRunsTab = () => {
  const [runs, setRuns] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [runsRes, perfRes] = await Promise.all([
          AZURE.get('/editorial/agent-runs', { params: { limit: 20 } }),
          AZURE.get('/editorial/agent-performance'),
        ]);
        setRuns(runsRes.data.data || []);
        setPerformance(perfRes.data.data || []);
      } catch (err) {
        console.error('Error fetching agent runs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return (
    <div className="space-y-6">
      {/* Performance summary cards */}
      {performance.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {performance.map((p, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-400 uppercase">{p.agent_name} • {p.run_type}</p>
              <div className="mt-2 space-y-1">
                <p className="text-lg font-bold" style={{ color: COLORS.primary }}>{p.total_runs || 0} runs</p>
                <p className="text-xs text-gray-500">
                  {p.total_items_created || 0} itens • {p.total_tokens || 0} tokens
                </p>
                <p className="text-xs text-gray-500">
                  💰 ${(p.total_cost_usd || 0).toFixed(4)} USD
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Runs table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                <th className="px-4 py-3 text-left">Agente</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Itens</th>
                <th className="px-4 py-3 text-center">Tokens</th>
                <th className="px-4 py-3 text-center">Custo</th>
                <th className="px-4 py-3 text-left">Data</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{r.agent_name}</td>
                  <td className="px-4 py-3 text-gray-600">{r.run_type}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-center text-gray-600">
                    {r.items_created || 0}/{r.items_processed || 0}
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-xs text-gray-500">
                    {r.total_tokens || 0}
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-xs text-gray-500">
                    ${(r.cost_usd || 0).toFixed(4)}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {r.started_at ? new Date(r.started_at).toLocaleString('pt-BR') : '—'}
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    Nenhuma execução registrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── MAIN PAGE ──────────────────────────────────────────────
const GovernanceEditorialEnginePage = () => {
  const [activeTab, setActiveTab] = useState('insights');
  const [health, setHealth] = useState(null);
  const { lang } = useGovernanceLang();
  const t = EDITORIAL_LABELS[lang] || EDITORIAL_LABELS.pt;

  useEffect(() => {
    AZURE.get('/editorial/health')
      .then(({ data }) => setHealth(data))
      .catch(() => setHealth({ success: false }));
  }, []);

  // Render active tab with props when needed
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'insights':
        return <InsightsTab onNavigateToDrafts={() => setActiveTab('drafts')} t={t} />;
      case 'drafts':
        return <DraftsTab />;
      case 'publish':
        return <PublishQueueTab />;
      case 'templates':
        return <TemplatesTab />;
      case 'library':
        return <LibraryTab />;
      case 'agent-runs':
        return <AgentRunsTab />;
      default:
        return <InsightsTab onNavigateToDrafts={() => setActiveTab('drafts')} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <h1 className="text-gray-900 text-2xl font-extrabold">
              Editorial Engine
            </h1>
            <p className="text-gray-500 mt-1">
              {t.subtitle}
            </p>
          </div>
        </div>
        {health && (
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`w-2.5 h-2.5 rounded-full ${health.success ? 'bg-green-500' : 'bg-red-500'}`}
            />
            <span className="text-xs text-gray-500">
              {health.success ? t.healthOk : t.healthOff}
            </span>
            {health.success && (
              <span className="text-xs text-gray-400">
                v{health.version} • {health.insight_keys?.length || 0} detectors
              </span>
            )}
          </div>
        )}
      </div>

      {/* Stats bar */}
      {health?.success && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <Lightbulb className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-lg font-bold" style={{ color: COLORS.primary }}>
                {health.insight_keys?.length || 0}
              </p>
              <p className="text-xs text-gray-500">{t.detectors}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-lg font-bold" style={{ color: COLORS.primary }}>
                {health.channels?.length || 0}
              </p>
              <p className="text-xs text-gray-500">{t.channels}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <Activity className="w-5 h-5 text-purple-500" />
            <div>
              <p className="text-lg font-bold" style={{ color: COLORS.primary }}>
                {health.endpoints?.length || 0}
              </p>
              <p className="text-xs text-gray-500">{t.endpoints}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 ${
                isActive
                  ? 'border-current text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              style={isActive ? { color: COLORS.primary } : {}}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {renderActiveTab()}
    </div>
  );
};

export default GovernanceEditorialEnginePage;
